import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Button, ButtonVariant, IconPosition, Lightbox } from '../../styles';
import {
    FrequenceDureeTerme,
    getYearsOrMonths,
    TEXTE_DYNAMIQUE,
    TypeOffre
} from '../../hooks';
import { getKeyTitre } from './DetailsOffreModal.utils';
import themePalette from '../../theme/themePalette';
import { Box } from '@material-ui/core';
import {
    TextCalculTaux,
    TextCarateristique,
    TextExemplesVariations,
    TextPourquoiChoisir,
    TextToleranceVariation,
    TitreCalculTaux,
    TitreCaracteristiques,
    TitreExemplesVariations,
    TitrePourquoiChoisir,
    TitreToleranceVariation
} from './sectionsModal';

interface DetailsOffreModalProps {
    valeurDureeTerme: number;
    frequenceDureeTerme: FrequenceDureeTerme;
    typeOffre: TypeOffre;
    tauxRabaisSubsequent: number;
    buttonLabel: string;
    buttonDataTestId?: string;
    titreOffre: string;
    onOpenModalAnalytic: () => void;
}

const useStyles = makeStyles(() =>
    createStyles({
        titre: {
            textAlign: 'left'
        },
        sousTitre: {
            color: themePalette.primary['desjardinsVert'],
            fontSize: '18px !important'
        },
        text: {
            marginBottom: '8px',
            marginLeft: '16px'
        },
        firstText: {
            marginTop: '10px'
        },
        buttonDetailsOffre: {
            textAlign: 'center'
        }
    })
);

export const DetailsOffreModal: React.FC<DetailsOffreModalProps> = ({
    valeurDureeTerme,
    frequenceDureeTerme,
    typeOffre,
    buttonLabel,
    buttonDataTestId,
    titreOffre,
    onOpenModalAnalytic
}) => {
    const classes = useStyles();
    const [titrePrefix, setTitrePrefix] = useState<string>('');
    const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
    const [t] = useTranslation(['DetailsOffreModal']);

    const renderCaracteristiques = () => {
        return (
            <>
                <TitreCaracteristiques
                    typeOffre={typeOffre}
                    cssClasse={classes.sousTitre}
                />
                <TextCarateristique typeOffre={typeOffre} />
            </>
        );
    };

    const renderTolerenceVariation = () => {
        return (
            <>
                <TitreToleranceVariation cssClasse={classes.sousTitre} />
                <TextToleranceVariation typeOffre={typeOffre} />
            </>
        );
    };

    const renderCaculTauxPersonnalise = () => {
        if (
            typeOffre === TypeOffre.CINQ_ANS_VARIABLE_REDUIT ||
            typeOffre === TypeOffre.CINQ_ANS_VARIABLE_PROTEGE ||
            typeOffre === TypeOffre.CINQ_ANS_REVISABLE_FERME
        ) {
            return (
                <>
                    <TitreCalculTaux cssClasse={classes.sousTitre} />
                    <TextCalculTaux typeOffre={typeOffre} />
                </>
            );
        }
    };

    const renderPourquoiChoisir = () => {
        const frequenceAfficher: string = getYearsOrMonths(
            valeurDureeTerme,
            frequenceDureeTerme,
            false
        );

        return (
            <>
                <TitrePourquoiChoisir cssClasse={classes.sousTitre} />
                <TextPourquoiChoisir
                    typeOffre={typeOffre}
                    frequence={frequenceAfficher}
                />
            </>
        );
    };

    const renderExemplesVariations = () => {
        if (
            typeOffre === TypeOffre.CINQ_ANS_VARIABLE_REDUIT ||
            typeOffre === TypeOffre.CINQ_ANS_VARIABLE_PROTEGE ||
            typeOffre === TypeOffre.CINQ_ANS_REVISABLE_FERME
        ) {
            return (
                <>
                    <TitreExemplesVariations cssClasse={classes.sousTitre} />
                    <TextExemplesVariations typeOffre={typeOffre} />
                </>
            );
        }
    };

    // Quand la modale ouvre, on envoie des analytiques
    const handleOpenModal = () => {
        setIsModalOpened(true);
        onOpenModalAnalytic();
    };

    useEffect(() => {
        if (isModalOpened) {
            if (TEXTE_DYNAMIQUE.includes(typeOffre)) {
                setTitrePrefix(
                    getYearsOrMonths(valeurDureeTerme, frequenceDureeTerme)
                );
            } else {
                setTitrePrefix('');
            }
        }
    }, [isModalOpened]);

    return (
        <>
            <Box ml="1rem">
                <Button
                    dataTestId={buttonDataTestId}
                    variant={ButtonVariant.tertiary}
                    iconName="navigations_contour_modale"
                    iconPosition={IconPosition.start}
                    onClick={handleOpenModal}
                    srOnlySuffix={
                        t('boutonOuvrirSRPrefix') +
                        titreOffre +
                        t('boutonOuvrirSRSuffix')
                    }
                >
                    {buttonLabel}
                </Button>
            </Box>
            <Lightbox
                key={typeOffre}
                show={isModalOpened}
                title={titrePrefix + t(getKeyTitre(typeOffre))}
                buttonPrimaryLabel={t('boutonFermerDetailsOffre')}
                onClose={() => setIsModalOpened(false)}
                onClickPrimaryButton={() => setIsModalOpened(false)}
            >
                {renderCaracteristiques()}
                {renderTolerenceVariation()}
                {renderCaculTauxPersonnalise()}
                {renderExemplesVariations()}
                {renderPourquoiChoisir()}
            </Lightbox>
        </>
    );
};
